import { useEffect, useState, type ReactNode } from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router';
import { Box, Button, CircularProgress, Drawer, Typography } from '@mui/material';
import { useNotification } from '@/hooks/useNotification';
import { useDrawerDraftStore, type DrawerDraftConfig } from '@/stores/drawerDraftStore';
import { osirisTokens } from '@/theme/tokens';

const PURPLE_FILL = '#6e44f5';
const PURPLE_BORDER = '#826cfc';

interface DrawerShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  headerAction?: ReactNode;
  width?: number | string;
  className?: string;
  children: ReactNode;
  footer?: ReactNode;
  tabs?: ReactNode;
  scrollable?: boolean;
  // Unsaved input: outside click / Esc no longer closes (the panel nudges
  // instead), and leaving the page offers a "Go back" that restores `draft`
  dirty?: boolean;
  draft?: DrawerDraftConfig;
}

// Right-panel width per design: 400px below 1600px viewports, 460px above
// (Figma To Do 28462:21769)
const RESPONSIVE_WIDTH = {
  width: { xs: '100%', sm: 400 },
  '@media (min-width:1600px)': { width: 460 },
};

const PANEL_SHADOW = '-12px 0 32px -8px rgba(0, 0, 0, 0.45)';

// Two identical keyframes — alternating the name restarts the animation on
// every blocked click without remounting the paper
const nudgeFrames = {
  '0%, 100%': { transform: 'translateX(0)', boxShadow: PANEL_SHADOW },
  '15%, 70%': { boxShadow: `inset 0 0 0 1px ${osirisTokens.accent.mid}, ${PANEL_SHADOW}` },
  '20%': { transform: 'translateX(-6px)' },
  '40%': { transform: 'translateX(5px)' },
  '60%': { transform: 'translateX(-3px)' },
  '80%': { transform: 'translateX(2px)' },
};

function UnsavedChangesGuard({ draft }: { draft?: DrawerDraftConfig }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification, hideNotification } = useNotification();
  const stash = useDrawerDraftStore((s) => s.stash);
  const requestRestore = useDrawerDraftStore((s) => s.requestRestore);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    const returnTo = location.pathname + location.search;
    if (draft) {
      stash({ key: draft.key, entity: draft.entity, values: draft.getValues(), returnTo });
    }
    showNotification(
      {
        title: 'Unsaved changes',
        subtitle: (
          <Box
            component="button"
            type="button"
            className="unsaved-changes-go-back"
            onClick={() => {
              hideNotification();
              requestRestore();
              navigate(returnTo);
            }}
            sx={{
              p: 0,
              border: 0,
              bgcolor: 'transparent',
              color: osirisTokens.text.primary,
              font: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Go back
          </Box>
        ),
      },
      'info',
    );
    blocker.proceed();
  }, [
    blocker,
    draft,
    location,
    navigate,
    stash,
    requestRestore,
    showNotification,
    hideNotification,
  ]);

  return null;
}

export function DrawerShell({
  open,
  onClose,
  title,
  headerAction,
  width,
  className,
  children,
  footer,
  tabs,
  scrollable = true,
  dirty = false,
  draft,
}: DrawerShellProps) {
  const [nudgeCount, setNudgeCount] = useState(0);
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    setNudgeCount(0);
  }

  const handleDrawerClose = () => {
    if (dirty) {
      setNudgeCount((n) => n + 1);
      return;
    }
    onClose();
  };

  // Header and side nav stay outside the modal so section navigation works
  // even while a dirty panel ignores outside clicks
  const overlayInset = {
    top: `${osirisTokens.layout.headerHeight}px`,
    left: { xs: 0, sm: `${osirisTokens.layout.sidebarWidth}px` },
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      disableScrollLock
      disableEnforceFocus
      className={className}
      slotProps={{
        root: { sx: overlayInset },
        backdrop: { sx: overlayInset },
        paper: {
          className: nudgeCount > 0 ? 'drawer-shell-nudge' : undefined,
          sx: {
            '@keyframes drawer-shell-nudge-a': nudgeFrames,
            '@keyframes drawer-shell-nudge-b': nudgeFrames,
            ...(nudgeCount > 0 && {
              animation: `drawer-shell-nudge-${nudgeCount % 2 ? 'a' : 'b'} 420ms ease-out`,
            }),
            top: `${osirisTokens.layout.headerHeight}px`,
            height: `calc(100% - ${osirisTokens.layout.headerHeight}px)`,
            ...(width !== undefined ? { width: { xs: '100%', sm: width } } : RESPONSIVE_WIDTH),
            maxWidth: '100vw',
            boxShadow: PANEL_SHADOW,
            bgcolor: osirisTokens.bg.page,
            backgroundImage: `linear-gradient(0deg, ${osirisTokens.alpha.dark100}, ${osirisTokens.alpha.dark100})`,
            borderLeft: `1px solid ${osirisTokens.border.primary}`,
          },
        },
      }}
    >
      {open && dirty && <UnsavedChangesGuard draft={draft} />}
      <Box
        className="drawer-shell-root"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <Box
          className="drawer-shell-header"
          sx={{
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pl: 2,
            pr: 1,
            bgcolor: osirisTokens.bg.header,
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: osirisTokens.fonts.display,
              fontSize: 13,
              fontWeight: 400,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: osirisTokens.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
          {headerAction}
        </Box>

        {tabs}

        <Box
          className="drawer-shell-content"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: scrollable ? 'auto' : 'hidden',
            display: 'flex',
            flexDirection: 'column',
            pb: footer ? 12 : 0,
          }}
        >
          {children}
        </Box>

        {footer && (
          <Box
            className="drawer-shell-footer"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              px: 2,
              pt: 3,
              pb: 2,
              display: 'flex',
              gap: 1.25,
              background:
                'linear-gradient(to top, #212121 0%, #212121 60%, rgba(33, 33, 33, 0) 100%)',
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

interface DrawerFooterButtonsProps {
  onCancel: () => void;
  isLoading?: boolean;
  saveText?: string;
  cancelText?: string;
  formId?: string;
  disabled?: boolean;
  onSave?: () => void;
}

export function DrawerFooterButtons({
  onCancel,
  isLoading,
  saveText = 'Save Changes',
  cancelText = 'Cancel',
  formId,
  disabled,
  onSave,
}: DrawerFooterButtonsProps) {
  return (
    <>
      <Button
        variant="outlined"
        onClick={onCancel}
        className="drawer-shell-cancel"
        sx={{
          flex: 1,
          height: 40,
          borderRadius: '2px',
          textTransform: 'none',
          fontFamily: osirisTokens.fonts.body,
          fontSize: 14,
          fontWeight: 500,
        }}
      >
        {cancelText}
      </Button>
      <Button
        variant="contained"
        type={formId ? 'submit' : 'button'}
        form={formId}
        onClick={onSave}
        disabled={isLoading || disabled}
        className="drawer-shell-save"
        startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
        sx={{
          flex: 1,
          height: 40,
          borderRadius: '2px',
          bgcolor: PURPLE_FILL,
          color: osirisTokens.text.primary,
          textTransform: 'none',
          fontFamily: osirisTokens.fonts.body,
          fontSize: 14,
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': { bgcolor: PURPLE_BORDER, boxShadow: 'none' },
          '&.Mui-disabled': {
            bgcolor: 'rgba(255, 255, 255, 0.12)',
            color: 'rgba(255, 255, 255, 0.3)',
          },
        }}
      >
        {isLoading ? 'Saving…' : saveText}
      </Button>
    </>
  );
}
